<script setup>
import { isEqual, pick, omit } from 'lodash-es'
import { getCurrentInstance, computed, ref } from 'vue'
import ExportMenu from '@/components/ExportMenu.vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import TableControls from '@/components/report-page-layouts/TableControls.vue'
import TransferMenu from '@/components/TransferMenu.vue'

const props = defineProps({
  displaySettings: {
    type: Object,
    required: true,
  },
  defaultDisplaySettings: { type: Object, required: true },
  visibleRowsCount: { type: Number, default: 0 },
  rowsCount: { type: Number, default: 0 },
  exportMenuProps: { type: Object, default: () => ({}) },
  canGroupByProperty: { type: Boolean, default: false },
  showWarningBadge: { type: Boolean, default: false },
  selectedChemicals: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:displaySettings'])

const onShowChemicalsWithNoEstimatesChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, showChemicalsWithNoEstimates: value })
}

const onShowChemCellChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, showChemCell: value })
}

const onShowVirtualizedChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, showVirtualized: value })
}

const onShowExperimentalChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, showExperimental: value })
}

const onShowPredictedChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, showPredicted: value })
}

const onIsGroupedByPropertyChange = (value) => {
  emit('update:displaySettings', { ...props.displaySettings, isGroupedByProperty: value })
}

const proxy = getCurrentInstance().proxy
const isMobile = computed(() => proxy.$vuetify.breakpoint.xs)

const columnToggleOptions = computed(() => [
  {
    key: 'showExperimental',
    inputValue: props.displaySettings.showExperimental,
    onChange: onShowExperimentalChange,
    label: 'Exp.',
    title: 'Show experimental data',
    show: true,
  },
  {
    key: 'showPredicted',
    inputValue: props.displaySettings.showPredicted,
    onChange: onShowPredictedChange,
    label: 'Pred.',
    title: 'Show predicted data',
    show: true,
  },
])

const activeToggleIndices = computed(() =>
  columnToggleOptions.value
    .map((toggle, index) => (toggle.inputValue ? index : undefined))
    .filter((index) => index !== undefined),
)

const switchOptions = computed(() => [
  {
    key: 'showChemicalsWithNoEstimates',
    inputValue: props.displaySettings.showChemicalsWithNoEstimates,
    onChange: onShowChemicalsWithNoEstimatesChange,
    label: 'Show Empty',
    title: 'Show chemicals with no predictions or errors in the table',
    show: true,
    disabled: false,
  },

  {
    key: 'showChemCell',
    inputValue: props.displaySettings.showChemCell,
    onChange: onShowChemCellChange,
    label: 'Show Structure',
    title: 'Show structure or show only chemical name',
    show: true,
    disabled: false,
  },

  {
    key: 'isGroupedByProperty',
    inputValue: props.displaySettings.isGroupedByProperty,
    onChange: onIsGroupedByPropertyChange,
    label: 'Group by property',
    title: 'Show data grouped by property',
    show: props.canGroupByProperty,
    disabled: false,
  },

  {
    key: 'showVirtualized',
    inputValue: props.displaySettings.showVirtualized,
    onChange: onShowVirtualizedChange,
    label: 'Virtualized view',
    title: 'Show all rows using virtualization',
    show: true,
    disabled: false,
  },
])

const visibleSwitchOptions = computed(() => switchOptions.value.filter((option) => option.show))

const isDisplaySettingsChanged = computed(() => {
  const defaultSettingsToCompare = props.canGroupByProperty
    ? props.defaultDisplaySettings
    : omit(props.defaultDisplaySettings, ['isGroupedByProperty'])

  const keysToCompare = Object.keys(defaultSettingsToCompare || {})

  const currentSettingsToCompare = pick(props.displaySettings, keysToCompare)

  return !isEqual(currentSettingsToCompare, defaultSettingsToCompare)
})
</script>

<template>
  <TableControls>
    <template #left>
      <slot name="left" />
    </template>

    <template #right>
      <v-menu
        v-if="rowsCount"
        offset-y
        :close-on-content-click="false"
        :max-height="isMobile ? '15rem' : '25rem'"
        :max-width="isMobile ? undefined : '20rem'"
      >
        <template #activator="{ on: menuOn, attrs: menuAttrs }">
          <v-tooltip
            top
            v-bind="defaultVTooltipProps"
          >
            <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <v-btn
                v-bind="{ ...menuAttrs, ...tooltipAttrs }"
                v-on="{ ...menuOn, ...tooltipOn }"
                aria-label="Show display options"
                class="border flat-button"
              >
                <v-badge
                  :value="!!isDisplaySettingsChanged"
                  dot
                  color="primary"
                  offset-x="0"
                  offset-y="0"
                >
                  <v-badge
                    :value="!!showWarningBadge"
                    dot
                    color="warning"
                    :offset-x="isDisplaySettingsChanged ? 10 : 0"
                    offset-y="0"
                  >
                    <v-icon>mdi-tune-variant</v-icon>
                  </v-badge>
                </v-badge>
              </v-btn>
            </template>

            <span>Show display options</span>
          </v-tooltip>
        </template>

        <v-card class="overflow-auto">
          <v-card-subtitle>Display options</v-card-subtitle>

          <v-divider class="mx-4 my-0" />
          <v-card-text class="pt-1">
            <div
              class="d-flex flex-column"
              style="gap: 1rem"
            >
              <ExportMenu
                v-if="visibleRowsCount && isMobile"
                v-bind="exportMenuProps"
                :ignore-formats="['SMI']"
                show-label
              />

              <TransferMenu
                v-if="visibleRowsCount && isMobile"
                :records="selectedChemicals"
                hide-add-button
                only-icon
              />

              <div class="d-flex flex-column">
                <div class="d-flex flex-column">
                  <span>Visible columns</span>
                  <v-btn-toggle
                    :value="activeToggleIndices"
                    dense
                    color="primary"
                    mandatory
                    multiple
                  >
                    <v-btn
                      v-for="(toggleLogic, index) in columnToggleOptions"
                      :key="toggleLogic.key"
                      :title="toggleLogic.title"
                      @click="
                        activeToggleIndices?.length === 1 && activeToggleIndices.includes(index)
                          ? undefined
                          : toggleLogic.onChange(!toggleLogic.inputValue)
                      "
                      :aria-label="toggleLogic.title"
                      class="flex-grow-1"
                    >
                      {{ toggleLogic.label }}
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <slot name="display-options-top" />

                <div
                  v-for="item in visibleSwitchOptions"
                  :key="item.key"
                  class="d-flex flex-row align-items-center"
                  style="gap: 0.25em"
                >
                  <v-switch
                    :id="item.key"
                    :input-value="item.inputValue"
                    :disabled="item.disabled"
                    @change="item.onChange"
                    :label="item.label"
                    :title="item.title"
                    dense
                    hide-details
                  />
                  <slot :name="`switch-${item.key}-right`" />
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-menu>

      <ExportMenu
        v-if="visibleRowsCount && !isMobile"
        v-bind="exportMenuProps"
        :ignore-formats="['SMI']"
      />

      <TransferMenu
        v-if="visibleRowsCount && !isMobile"
        :records="selectedChemicals"
        hide-add-button
        only-icon
      />

      <slot name="right" />
    </template>
  </TableControls>
</template>
